import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from '../../i18n/FormattedMessage';
import {
  ChatBody,
  PeopleList,
  StartChatModal,
  ChatRoomDetails,
} from './components';
import './ChatView.scss';
import { EVENTS, saveEvent } from '../../utils/eventAnalytics';

const LIMIT = 100;

const ChatView = (props) => {
  const {
    // data props
    chats,
    allPeople,
    peopleList,
    currentUser,
    activeChatId,
    chatRoomDetails,
    page,
    totalAllPeopleCount,
    totalChatMessagesCount,
    // action props
    getGroupDetails,
    createGroupChat,
    setActiveChatId,
    sendTextMessage,
    updateGroupChat,
    getAllChatMessages,
    getAllPeopleToChat,
    createIndividualChat,
    deleteOldMessage,
    // flag props
    isChatLoading,
    isGetChatByIdSuccess,
    isAllPeopleApiLoading,
    isAllPeopleApiError,
    allPeopleApiError,
    isUpdateChatLoading,
    isUpdateChatSuccess,
    isUpdateChatError,
    updateChatError,
    isChatRoomDetailsLoading,
    // router
    location,
  } = props;

  const [isChatModalVisible, setChatModalVisible] = useState(false);
  const [isGroupChatEnabled, setGroupChatEnabled] = useState(false);
  const [isChatRoomDetailsVisible, setChatRoomDetailsVisible] = useState(false);

  // While the chat view is mounted, turn the scrollable content container into
  // a full-height flex column so the chat fills the space with no grey gap and
  // the page itself does not scroll. The height is derived dynamically from the
  // container's distance to the top of the viewport, so it adapts to any screen
  // size and to whatever the app header height is.
  useEffect(() => {
    const main = document.getElementById('scrollableDiv');
    if (!main) return undefined;
    main.classList.add('chat-route-active');
    const applyHeight = () => {
      const top = main.getBoundingClientRect().top;
      main.style.setProperty(
        '--chat-main-height',
        `${window.innerHeight - top}px`
      );
    };
    applyHeight();
    window.addEventListener('resize', applyHeight);
    return () => {
      window.removeEventListener('resize', applyHeight);
      main.classList.remove('chat-route-active');
      main.style.removeProperty('--chat-main-height');
    };
  }, []);

  // Honor the intent passed from the header chat popover: preselect
  // Individuals/Group, open an existing chat, or start a new one.
  useEffect(() => {
    const navState = location && location.state;
    if (!navState) return;
    if (typeof navState.isGroup === 'boolean') {
      setGroupChatEnabled(navState.isGroup);
    }
    if (navState.chatId) {
      setActiveChatId({ chatId: navState.chatId });
    }
    if (navState.openStartModal) {
      setChatModalVisible(true);
    }
    // Run once on mount for the incoming navigation intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showChatModal = () => {
    const event = isGroupChatEnabled
      ? EVENTS.CHAT_GROUP_START_NEW_CHAT
      : EVENTS.CHAT_INDIVIDUALS_START_NEW_CHAT;
    saveEvent(event, {});
    setChatModalVisible(true);
  };

  const hideChatModal = () => {
    setChatModalVisible(false);
  };

  const showGroupChats = (enabled) => {
    setGroupChatEnabled(enabled);
    setActiveChatId('');
  };

  const showChatRoomDetails = () => {
    setChatRoomDetailsVisible(true);
    getAllPeopleToChat(false, 0, LIMIT);
    getGroupDetails({ chatId: activeChatId });
  };

  const hideChatRoomDetails = () => {
    setChatRoomDetailsVisible(false);
  };

  const commonModalProps = {
    allPeople,
    page,
    totalAllPeopleCount,
    isGroupChatEnabled,
    getAllPeopleToChat,
    isAllPeopleApiLoading,
    isAllPeopleApiError,
    allPeopleApiError,
    createIndividualChat,
    isUpdateChatLoading,
    isUpdateChatSuccess,
    currentUser,
    createGroupChat,
    updateChatError,
    isUpdateChatError,
  };

  return (
    <Fragment>
      <div className="chat-wrap">
        {/* Modal */}
        {
          <StartChatModal
            isVisible={isChatModalVisible}
            hideChatModal={hideChatModal}
            {...commonModalProps}
          />
        }
        {/* Modal */}
        {
          <ChatRoomDetails
            isVisible={isChatRoomDetailsVisible}
            hideChatModal={hideChatRoomDetails}
            chatRoomDetails={chatRoomDetails}
            getGroupDetails={getGroupDetails}
            activeChatId={activeChatId}
            currentUser={currentUser}
            updateGroupChat={updateGroupChat}
            {...commonModalProps}
          />
        }
        {/* Left Pane */}
        <PeopleList
          peopleList={peopleList}
          activeChatId={activeChatId}
          setActiveChatId={setActiveChatId}
          showChatModal={showChatModal}
          showGroupChats={showGroupChats}
          isGroupChatEnabled={isGroupChatEnabled}
          onGroupClick={() => showGroupChats(!isGroupChatEnabled)}
        />
        {/* Right Pane */}
        <ChatBody
          chats={chats}
          activeChatId={activeChatId}
          peopleList={peopleList}
          sendTextMessage={sendTextMessage}
          isChatLoading={isChatLoading}
          totalChatMessagesCount={totalChatMessagesCount}
          isUpdateChatLoading={isUpdateChatLoading}
          getAllChatMessages={getAllChatMessages}
          currentUser={currentUser}
          isGetChatByIdSuccess={isGetChatByIdSuccess}
          getGroupDetails={getGroupDetails}
          showChatRoomDetails={showChatRoomDetails}
          isChatRoomDetailsLoading={isChatRoomDetailsLoading}
          setActiveChatId={setActiveChatId}
          deleteOldMessage={deleteOldMessage}
          isGroupChatEnabled={isGroupChatEnabled}
        />
      </div>
      <div className="chat-encrypted-footer">
        <span className="chat-encrypted-icon material-icons-outlined">
          lock
        </span>
        <FormattedMessage
          id="chat.endToEndEncrypted"
          defaultMessage="End-to-end encrypted"
        />
      </div>
    </Fragment>
  );
};

ChatView.propTypes = {
  activeChatId: PropTypes.string.isRequired,
  allPeople: PropTypes.array.isRequired,
  page: PropTypes.number.isRequired,
  totalAllPeopleCount: PropTypes.number.isRequired,
  allPeopleApiError: PropTypes.string.isRequired,
  chatRoomDetails: PropTypes.object.isRequired,
  chats: PropTypes.array.isRequired,
  createGroupChat: PropTypes.func.isRequired,
  createIndividualChat: PropTypes.func.isRequired,
  currentUser: PropTypes.object.isRequired,
  getAllChatMessages: PropTypes.func.isRequired,
  getAllPeopleToChat: PropTypes.func.isRequired,
  getGroupDetails: PropTypes.func.isRequired,
  isAllPeopleApiError: PropTypes.bool.isRequired,
  isAllPeopleApiLoading: PropTypes.bool.isRequired,
  isChatLoading: PropTypes.bool.isRequired,
  totalChatMessagesCount: PropTypes.number.isRequired,
  isChatRoomDetailsLoading: PropTypes.bool.isRequired,
  isGetChatByIdSuccess: PropTypes.bool.isRequired,
  isUpdateChatError: PropTypes.bool.isRequired,
  isUpdateChatLoading: PropTypes.bool.isRequired,
  isUpdateChatSuccess: PropTypes.bool.isRequired,
  peopleList: PropTypes.array.isRequired,
  sendTextMessage: PropTypes.func.isRequired,
  setActiveChatId: PropTypes.func.isRequired,
  updateChatError: PropTypes.func.isRequired,
  updateGroupChat: PropTypes.func.isRequired,
  deleteOldMessage: PropTypes.func.isRequired,
  location: PropTypes.object,
};

export default ChatView;
